import {
    deserialize,
    type Fragment,
    type FragmentJSON,
    type NodeJSON,
    type NodeType,
} from '@gitbook/slate';
import { IS_IE } from '@gitbook/slate-dev-environment';
import { assertIsDefined } from '@gitbook/util-ts';

import { HANDLE_CODE_METADATA, TRANSFER_TYPES, TRANSFER_TYPES_CODE } from '../constants';
import { decodeFromBase64 } from './base64';

type TransferData = {
    files: File[] | undefined;
    node: NodeType | undefined;
    fragment: Fragment | undefined;
    html: string | null;
    rich: string | null;
    text: string | null;

    /**
     * Allow plugins in application land to pass additional data, mapped by content type.
     */
    others: {
        [type: string]: unknown;
    };
};

type Transfer = TransferData & { type: ReturnType<typeof getTransferType> };

const { FRAGMENT, HTML, NODE, RICH, TEXT } = TRANSFER_TYPES;

/*
 * Fragment matching regexp for HTML nodes.
 */
const FRAGMENT_MATCHER = / data-slate-fragment="([^\s"]+)"/;

/*
 * Get the transfer data from an `event`.
 *
 * Does this in three steps
 *  1. Read the input (clipboard)
 *  2. Transform
 *  3. Determine how to parse
 */
export default function getEventTransfer(
    event: DragEvent | ClipboardEvent,
    options: { ignoreMetadata?: boolean } = { ignoreMetadata: false }
): Transfer {
    // COMPAT: IE 11 doesn't populate nativeEvent with either
    // dataTransfer or clipboardData. We'll need to use the base event
    // object (2018/14/6)
    if (!IS_IE && 'nativeEvent' in event && event.nativeEvent) {
        // @ts-expect-error
        event = event.nativeEvent;
    }

    // 1. READ INPUT
    const transfer = 'dataTransfer' in event ? event.dataTransfer : event.clipboardData;
    assertIsDefined(transfer, 'no data transfer defined for clipboard/drag event');
    let fragmentString = getType(transfer, FRAGMENT);
    let nodeString = getType(transfer, NODE);
    const html = getType(transfer, HTML);
    const rich = getType(transfer, RICH);
    let text = getType(transfer, TEXT);
    let node: NodeType | undefined;
    let fragment: Fragment | undefined;
    let files: File[] | undefined;
    const codeMetadata = getCodeMetadata(transfer); // code metadata

    // 2. TRANSFORM

    // If there isn't a fragment, but there is HTML, check to see if the HTML is
    // actually an encoded fragment.
    if (!fragmentString && html && html.indexOf(' data-slate-fragment="') >= 0) {
        const matches = FRAGMENT_MATCHER.exec(html);
        const encoded = matches?.[1];
        if (encoded) {
            fragmentString = encoded;
        }
    }

    // COMPAT: Edge doesn't handle custom data types
    // These will be embedded in text/plain in this case (2017/7/12)
    if (text) {
        const embeddedTypes = getEmbeddedTypes(text);

        if (embeddedTypes[FRAGMENT]) {
            fragmentString = embeddedTypes[FRAGMENT];
        }
        if (embeddedTypes[NODE]) {
            nodeString = embeddedTypes[NODE];
        }
        if (embeddedTypes[TEXT]) {
            text = embeddedTypes[TEXT];
        }
    }

    // If there's some known code metadata (e.g vscode) treat 'text' as code
    if (!options.ignoreMetadata && text && codeMetadata) {
        const langs = codeMetadata.map(({ type, data }) =>
            HANDLE_CODE_METADATA[type].getLang(data)
        );
        const lang = langs[0] ?? '';
        if (lang !== 'markdown') {
            // transform text into a markdown code fenced block
            text = `\`\`\`${lang ?? ''}\n${text}\n\`\`\``;
        }
    }

    // Decode a fragment or node if they exist.
    if (fragmentString) {
        fragment = decodeSlateTransferType<FragmentJSON, Fragment>(fragmentString);
    }
    if (nodeString) {
        node = decodeSlateTransferType<NodeJSON, NodeType>(nodeString);
    }

    // COMPAT: Edge sometimes throws 'NotSupportedError'
    // when accessing `transfer.items` (2017/7/12)
    try {
        // Get and normalize files if they exist.
        if (transfer.items?.length) {
            // @ts-expect-error
            files = Array.from(transfer.items)
                .map((item) => (item.kind === 'file' ? item.getAsFile() : null))
                .filter((exists) => exists);
        } else if (transfer.files?.length) {
            files = Array.from(transfer.files);
        }
    } catch {
        if (transfer.files?.length) {
            files = Array.from(transfer.files);
        }
    }

    // 3. DETERMINE BEST TYPE

    // Determine the type of the data.
    const data: Transfer = {
        files,
        fragment,
        html,
        node,
        rich,
        text,
        type: 'unknown',
        others: {},
    };
    data.type = getTransferType(data, codeMetadata);

    // 4. EXTRACT ADDITIONAL DATA
    // Extract any additional data that was passed in by plugins.
    const knownTypes = Object.values(TRANSFER_TYPES);
    const otherTypes = Array.from(transfer.types).filter((type) => !knownTypes.includes(type));

    const others = {};
    for (const type of otherTypes) {
        const data = transfer.getData(type);

        if (!data) {
            continue;
        }

        others[type] = data;
    }

    data.others = others;

    return data;
}

function decodeSlateTransferType<TJson extends NodeJSON, TNode extends NodeType>(
    content: string
): TNode | undefined {
    try {
        const decoded = decodeFromBase64<TJson>(content);

        // Ignore Slate-like payloads that do not match this serializer version.
        if (!decoded || typeof decoded !== 'object' || !('object' in decoded)) {
            return undefined;
        }

        // @ts-expect-error
        return deserialize(decoded) as TNode;
    } catch {
        return undefined;
    }
}

/*
 * Takes text input, checks whether contains embedded data
 * and returns object with original text +/- additional data
 */

function getEmbeddedTypes(text: string): { [key: string]: string } {
    const prefix = 'SLATE-DATA-EMBED::';

    if (text.substring(0, prefix.length) !== prefix) {
        return { TEXT: text };
    }

    // Attempt to parse, if fails then just standard text/plain
    // Otherwise, already had data embedded
    try {
        return JSON.parse(text.substring(prefix.length));
    } catch {
        throw new Error('Unable to parse custom Slate drag event data.');
    }
}

/*
 * Get the type of a transfer from its `data`.
 * Order is important:
 *  1. internal source
 *  2. external source
 *  3. files
 *  4. html
 *  5. text
 *
 * @param {Object} data
 * @return {String}
 */

function getTransferType(data: TransferData, codeMetadata: CodeMetadataTypes | null) {
    const internalSourceType = detectInternalSourceType(data);
    if (internalSourceType) {
        return internalSourceType;
    }

    const externalSourceType = detectExternalSourceType(data, codeMetadata);
    if (externalSourceType) {
        return externalSourceType;
    }

    if (data.files?.length) {
        return 'files';
    }
    if (data.html) {
        return 'html';
    }
    if (data.text) {
        return 'text';
    }
    return 'unknown';
}
/**
 * trasfer data is either fragment or node, then it's internal source
 */
function detectInternalSourceType(data: TransferData) {
    if (data.fragment) {
        return 'fragment';
    }
    if (data.node) {
        return 'node';
    }
}

/**
 * Cheaply detect if the transfer data is from an external source
 * in order to bypass the parsing order file > html > text
 *
 */
function detectExternalSourceType(data: TransferData, code: CodeMetadataTypes | null) {
    const sources = [
        // Microsoft office

        // COMPAT: Microsoft Word adds an image of the selected text to the data.
        // Since files are preferred over HTML or text, this would cause the type to
        // be considered `files`. But it also adds rich text data so we can check
        // for that and properly set the type to `html` or `text`. (2016/11/21)

        // Relying on data.html?.includes('urn:schemas-microsoft-com:office:word')
        // or data.html?.includes('urn:schemas-microsoft-com:office:excel') should also do the trick
        // If so, we can remove data.rich as it's not used anywhere else
        {
            type: 'html',
            check: () => data.rich && data.html,
        },
        {
            type: 'html',
            check: () => data.html?.includes('urn:schemas-microsoft-com:office'),
        },
        {
            type: 'text',
            check: () => data.rich && data.text,
        },

        // VSCode

        // Rely on special content type 'vscode-editor-data' for detection
        {
            type: 'text',
            check: () => data.text && code?.length,
        },
    ];

    const source = sources.find(({ check }) => check());

    return source?.type;
}

/*
 * Get one of types `TYPES.FRAGMENT`, `TYPES.NODE`, `text/html`, `text/rtf` or
 * `text/plain` from transfers's `data` if possible, otherwise return null.
 *
 * @param {Object} transfer
 * @param {String} type
 * @return {String}
 */

function getType(transfer: DataTransfer, type: string): string | null {
    if (!transfer.types || !transfer.types.length) {
        // COMPAT: In IE 11, there is no `types` field but `getData('Text')`
        // is supported`. (2017/06/23)
        return type === TEXT ? transfer.getData('Text') || null : null;
    }

    // COMPAT: In Edge, transfer.types doesn't respond to `indexOf`. (2017/10/25)
    const types = Array.from(transfer.types);

    return types.indexOf(type) !== -1 ? transfer.getData(type) || null : null;
}

type CodeMetadataTypes = { type: string; data: string }[];

/**
 * Try to parse a set of known content types that code editors usually add to the clipboard
 */
function getCodeMetadata(transfer: DataTransfer): CodeMetadataTypes | null {
    const matchedType = TRANSFER_TYPES_CODE.map((extractedType) => {
        const data = getType(transfer, extractedType) || '';

        return {
            type: extractedType,
            data,
        };
    }).filter((content) => content.data);

    return matchedType.length > 0 ? matchedType : null;
}
